import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { TABLE_ICONS } from '../utils/tableIcons';
import './PreviewPage.css';

const PreviewPage = () => {
  const { type, id } = useParams();
  const [dateId, subcategoryId] = id ? id.split('|') : [null, null];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [title, setTitle] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadToDelete, setUploadToDelete] = useState(null);

  useEffect(() => {
    fetchPreviewData();
  }, []);

  const fetchPreviewData = useCallback(async () => {
    try {
      setLoading(true);
      let uploadsList = [];
      let pageTitle = '';

      if (type === 'todayTomorrow') {
        // Fetch uploads for specific date from todayTomorrow collection
        // id is the date in dd-mm-yyyy format
        
        // Fetch all subcategories for this date
        const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow', id, 'subcategories'));
        
        // For each subcategory, fetch its uploads
        for (const subDoc of subcategoriesSnapshot.docs) {
          const subData = subDoc.data();
          const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', id, 'subcategories', subDoc.id, 'upload'));
          
          uploadsSnapshot.forEach((uploadDoc) => {
            const uploadData = uploadDoc.data();
            uploadsList.push({
              id: uploadDoc.id,
              subcategoryId: subDoc.id,
              subcategoryName: subData.name || '',
              date: id,
              url: uploadData.downloadURL || uploadData.imageurl || '',
              imageurl: uploadData.downloadURL || uploadData.imageurl || '',
              downloadURL: uploadData.downloadURL || uploadData.imageurl || '',
              thumbnailImg: uploadData.thumbnailImg || null,
              fileName: uploadData.fileName || '',
              name: uploadData.name || '',
              fileType: uploadData.fileType || '',
              createdAt: uploadData.createdAt || '',
              updatedAt: uploadData.updatedAt || ''
            });
          });
        }

        pageTitle = `Preview Uploads for ${id}`;
      } else if (type === 'greetings') {
        // Fetch uploads for specific greeting category from upload subcollection
        const categoryDocRef = doc(db, 'greetings', id);
        const categoryDocSnap = await getDoc(categoryDocRef);
        
        if (categoryDocSnap.exists()) {
          const categoryData = categoryDocSnap.data();
          pageTitle = `Preview Uploads for ${categoryData.name || 'Category'}`;
          
          // Fetch uploads from the upload subcollection
          const uploadsSnapshot = await getDocs(collection(db, 'greetings', id, 'upload'));
          
          uploadsSnapshot.forEach((uploadDoc) => {
            const uploadData = uploadDoc.data();
            uploadsList.push({
              id: uploadDoc.id,
              subcategoryId: id,
              subcategoryName: categoryData.name || '',
              url: uploadData.downloadURL || uploadData.imageurl || '',
              imageurl: uploadData.downloadURL || uploadData.imageurl || '',
              downloadURL: uploadData.downloadURL || uploadData.imageurl || '',
              thumbnailImg: uploadData.thumbnailImg || null,
              fileName: uploadData.fileName || '',
              name: uploadData.name || '',
              fileType: uploadData.fileType || '',
              createdAt: uploadData.createdAt || '',
              updatedAt: uploadData.updatedAt || ''
            });
          });
        }
      } else if (type === 'trending') {
        // Fetch uploads for specific trending category from upload subcollection
        const categoryDocRef = doc(db, 'trending', id);
        const categoryDocSnap = await getDoc(categoryDocRef);
        
        if (categoryDocSnap.exists()) {
          const categoryData = categoryDocSnap.data();
          pageTitle = `Preview Uploads for ${categoryData.name || 'Category'}`;
          
          // Fetch uploads from the upload subcollection
          const uploadsSnapshot = await getDocs(collection(db, 'trending', id, 'upload'));
          
          uploadsSnapshot.forEach((uploadDoc) => {
            const uploadData = uploadDoc.data();
            uploadsList.push({
              id: uploadDoc.id,
              subcategoryId: id,
              subcategoryName: categoryData.name || '',
              url: uploadData.downloadURL || uploadData.imageurl || '',
              imageurl: uploadData.downloadURL || uploadData.imageurl || '',
              downloadURL: uploadData.downloadURL || uploadData.imageurl || '',
              thumbnailImg: uploadData.thumbnailImg || null,
              fileName: uploadData.fileName || '',
              name: uploadData.name || '',
              fileType: uploadData.fileType || '',
              createdAt: uploadData.createdAt || '',
              updatedAt: uploadData.updatedAt || ''
            });
          });
        }
      } else if (type === 'todayTomorrowSubcategory') {
        // Fetch uploads for specific subcategory from todayTomorrow collection
        // dateId and subcategoryId are extracted from the id parameter
        if (dateId && subcategoryId) {
          const subcategoryDocRef = doc(db, 'todayTomorrow', dateId, 'subcategories', subcategoryId);
          const subcategoryDocSnap = await getDoc(subcategoryDocRef);
          
          if (subcategoryDocSnap.exists()) {
            const subcategoryData = subcategoryDocSnap.data();
            pageTitle = `Preview Uploads for ${subcategoryData.name || 'Subcategory'} (${dateId})`;
            
            // Fetch uploads from the upload subcollection
            const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', dateId, 'subcategories', subcategoryId, 'upload'));
            
            uploadsSnapshot.forEach((uploadDoc) => {
              const uploadData = uploadDoc.data();
              uploadsList.push({
                id: uploadDoc.id,
                subcategoryId: subcategoryId,
                subcategoryName: subcategoryData.name || '',
                date: dateId,
                url: uploadData.downloadURL || uploadData.imageurl || '',
                imageurl: uploadData.downloadURL || uploadData.imageurl || '',
                downloadURL: uploadData.downloadURL || uploadData.imageurl || '',
                fileName: uploadData.fileName || '',
                name: uploadData.name || '',
                fileType: uploadData.fileType || '',
                createdAt: uploadData.createdAt || '',
                updatedAt: uploadData.updatedAt || ''
              });
            });
          }
        }
      } else if (type === 'today' || type === 'tomorrow') {
        // Fetch uploads for specific subcategory from today/tomorrow collections
        const collectionPath = `homescreen/${type}/subcategory`;
        const docRef = doc(db, collectionPath, id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          pageTitle = `Preview Uploads for ${data.categoryname || 'Subcategory'} (${type.charAt(0).toUpperCase() + type.slice(1)})`;
          
          if (data.uploads && typeof data.uploads === 'object') {
            Object.entries(data.uploads).forEach(([uploadId, uploadData]) => {
              uploadsList.push({
                id: uploadId,
                subcategoryId: id,
                subcategoryName: data.categoryname || '',
                date: data.date || '',
                url: uploadData.imageurl || '',
                imageurl: uploadData.imageurl || '',
                startdate: uploadData.startdate || '',
                enddate: uploadData.enddate || '',
                createdAt: data.createdAt || '',
                categoryPath: data.categoryPath || `/homescreen/${type}`
              });
            });
          }
        }
      }

      // Sort by createdAt (newest first)
      uploadsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // Log thumbnail availability for performance monitoring
      const withThumbnails = uploadsList.filter(upload => upload.thumbnailImg).length;
      const totalUploads = uploadsList.length;

      setUploads(uploadsList);
      setTitle(pageTitle);
    } catch (error) {
      console.error('Error fetching preview data:', error);
    } finally {
      setLoading(false);
    }
  }, [type, dateId, subcategoryId]);

  const handleImageClick = (url) => {
    window.open(url, '_blank');
  };

  const handleDeleteUpload = async () => {
    if (!uploadToDelete) return;

    try {
      setDeletingId(uploadToDelete.id);
      setShowDeleteConfirm(false);
      
      const upload = uploadToDelete;
      
      // Delete from Storage using the image URL
      if (upload.imageurl || upload.url || upload.downloadURL) {
        try {
          const imageUrl = upload.downloadURL || upload.imageurl || upload.url;
          const storageRef = ref(storage, imageUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Could not delete from storage:', storageError);
          // Continue with Firestore deletion even if storage deletion fails
        }
      }
      
      // Delete based on type
      if (type === 'greetings') {
        // Delete from greetings/{categoryId}/upload subcollection
        await deleteDoc(doc(db, 'greetings', upload.subcategoryId, 'upload', upload.id));
      } else if (type === 'trending') {
        // Delete from trending/{categoryId}/upload subcollection
        await deleteDoc(doc(db, 'trending', upload.subcategoryId, 'upload', upload.id));
      } else if (type === 'todayTomorrow') {
        // Delete from todayTomorrow/{date}/subcategories/{subcategoryId}/upload
        await deleteDoc(doc(db, 'todayTomorrow', upload.date, 'subcategories', upload.subcategoryId, 'upload', upload.id));
      } else if (type === 'todayTomorrowSubcategory') {
        // Delete from todayTomorrow/{date}/subcategories/{subcategoryId}/upload
        await deleteDoc(doc(db, 'todayTomorrow', upload.date, 'subcategories', upload.subcategoryId, 'upload', upload.id));
      } else {
        // Determine the collection path for other types (today/tomorrow legacy)
        let collectionPath;
        if (type === 'today') {
          collectionPath = 'homescreen/today/subcategory';
        } else if (type === 'tomorrow') {
          collectionPath = 'homescreen/tomorrow/subcategory';
        }
        
        // Remove the upload from the subcategory document's nested uploads
        const subcategoryRef = doc(db, collectionPath, upload.subcategoryId);
        await updateDoc(subcategoryRef, {
          [`uploads.${upload.id}`]: null
        });
      }
      
      setUploads(prev => prev.filter(item => item.id !== upload.id));
    } catch (error) {
      console.error('Error deleting preview upload:', error);
      alert('Error deleting image. Please try again.');
    } finally {
      setDeletingId(null);
      setUploadToDelete(null);
    }
  };

  const handleDeleteButtonClick = (event, upload) => {
    event.stopPropagation();
    setUploadToDelete(upload);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setUploadToDelete(null);
  };

  const handleGoBack = () => {
    navigate('/admin/uploads');
  };

  const displayTitle = title || (type === 'todayTomorrow' || type === 'todayTomorrowSubcategory' ? 'Preview Today+Tomorrow Uploads' : 'Preview Greeting Uploads');
  const fileCountLabel = uploads.length === 1 ? 'file' : 'files';

  if (loading) {
    return (
      <div className="preview-page">
        <div className="preview-loading">
          <div className="loading-spinner"></div>
          <p>Loading preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-page">
      <div className="preview-header">
        <div className="preview-header-content">
          <h2 className="preview-title">{displayTitle}</h2>
          <div className="preview-header-actions">
            <span className="preview-count">{uploads.length} {fileCountLabel}</span>
            <button onClick={handleGoBack} className="btn btn-secondary preview-back-btn">
              ← Back to Uploads
            </button>
          </div>
        </div>
      </div>

      <div className="preview-content">
        {uploads.length === 0 ? (
          <div className="no-images">
            <div className="no-images-icon">📁</div>
            <h3>No Files Found</h3>
            <p>No files uploaded yet for this {type === 'todayTomorrow' || type === 'todayTomorrowSubcategory' ? 'subcategory' : 'subcategory'}.</p>
          </div>
        ) : (
          <div className="images-grid">
            {uploads.map(upload => (
              <div key={upload.id} className="image-card">
                <div className="image-container" onClick={() => handleImageClick(upload.url)}>
                  {upload.fileType && upload.fileType.startsWith('video/') ? (
                    <video 
                      src={upload.url}
                      poster={upload.thumbnailImg}
                      className="preview-video"
                      controls
                      preload="metadata"
                    />
                  ) : (
                    <img 
                      src={upload.thumbnailImg || upload.url} 
                      alt={upload.name}
                      onError={(e) => {
                        // If thumbnail fails to load, fallback to original image
                        if (e.target.src !== upload.url && upload.url) {
                          e.target.src = upload.url;
                        }
                      }}
                      loading="lazy"
                    />
                  )}
                  <button
                    type="button"
                    className="image-delete-btn"
                    onClick={(event) => handleDeleteButtonClick(event, upload)}
                    disabled={deletingId === upload.id}
                    aria-label="Delete file"
                  >
                    {deletingId === upload.id ? '…' : TABLE_ICONS.DELETE}
                  </button>
                  <div className="image-overlay">
                    <button 
                      onClick={() => handleImageClick(upload.url)}
                      className="view-btn"
                    >
                      {TABLE_ICONS.VIEW} {upload.fileType && upload.fileType.startsWith('video/') ? 'Play Video' : 'View Full Size'}
                    </button>
                  </div>
                </div>
                <div className="image-info">
                  <h4>{upload.subcategoryName || upload.name || (upload.fileType && upload.fileType.startsWith('video/') ? 'Video' : 'Image')}</h4>
                  <div className="image-details">
                    {upload.size && (
                      <p className="image-size">{(upload.size / 1024).toFixed(1)} KB</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button 
                onClick={handleCancelDelete}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="delete-confirm-content">
              <div className="warning-icon">{TABLE_ICONS.WARNING}</div>
              <p>Are you sure you want to delete this file? This will also delete all uploads for this file.</p>
              <p className="warning-text">This action cannot be undone.</p>
              <div className="form-actions">
                <button onClick={handleCancelDelete} className="btn btn-secondary">
                  CANCEL
                </button>
                <button onClick={handleDeleteUpload} className="btn btn-danger">
                  DELETE FILE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewPage;
